/**
 * <s-live-hint> — shows a small floating message once the shopper scrolls past
 * the "Live video chat" button, inviting them to book a video slot.
 * Shown once per browser session; closing hides it for the session.
 *
 * Children:
 *   [data-live-anchor] — the element watched while scrolling (the button)
 *   [data-live-hint]   — the floating message (starts with `hidden`)
 *   [data-live-close]  — button that dismisses the message
 */
class SLiveHint extends HTMLElement {
  #observer;
  #ac;
  #timer;
  static KEY = 's-live-hint-closed';

  connectedCallback() {
    const anchor = this.querySelector('[data-live-anchor]');
    this.hint = this.querySelector('[data-live-hint]');
    if (!anchor || !this.hint || this.#closed()) return;

    this.#ac = new AbortController();
    this.querySelector('[data-live-close]')?.addEventListener('click', () => this.#dismiss(), { signal: this.#ac.signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      if (scrolledPast) this.#show();
      else this.#hide();
    });
    this.#observer.observe(anchor);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #show() {
    if (this.#closed()) return;
    clearTimeout(this.#timer);
    this.hint.hidden = false;
    requestAnimationFrame(() => this.hint.setAttribute('data-show', ''));
  }

  #hide() {
    this.hint.removeAttribute('data-show');
    this.#timer = setTimeout(() => { this.hint.hidden = true; }, 250);
  }

  #dismiss() {
    try { sessionStorage.setItem(SLiveHint.KEY, '1'); } catch {}
    this.#hide();
    this.#observer?.disconnect();
  }

  #closed() {
    try { return sessionStorage.getItem(SLiveHint.KEY) === '1'; } catch { return false; }
  }
}

if (!customElements.get('s-live-hint')) customElements.define('s-live-hint', SLiveHint);
